import { FC } from 'react';
import { useParams } from 'react-router';
import { ConnectedProps, connect } from 'react-redux';
import usePanels from './usePanels';
import ViewerSignalR from 'Components/UI/SignalR/viewer';
import TabsShell, { PathActiveEnum } from 'Components/UI/TabsShell';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import SimpleViewerComponent from 'Components/UI/SimpleViewerComponent';
import Header from './components/Header';
import LoadingRender from './components/LoadingRender';
import Image from './components/Image';
import { extensionsImage } from 'Utils/ViewerUtils';
import DropboxFolderEmbed from './components/DropboxFolderEmbed';
import { Container } from './styles';

const Panels: FC<Props> = ({
  fixedPanels,
  notFixedPanels,
  isLoading,
  panelsFileInfo,
  fileViewerAutodesk,
  userInfo,
  globalLoading,
  currentTenant,
  tenants,
}) => {
  const params = useParams();

  const {
    viewerGoogle,
    panel,
    hasDownloadPermission,
    activeSrcTypeOption,
    stringContent,
    isViewerRender,
    isInternalItem,
    isDropboxType,
    buttons,
    activeListButton,
    handleActiveListButton,
    handleOpenFolder,
    handleDownload,
  } = usePanels({
    panelId: params.panelId || '',
    csId: Number(params.constructionSiteId),
    fixedPanels,
    notFixedPanels,
    isLoading,
    panelsFileInfo,
    fileViewerAutodesk,
    userInfo,
    globalLoading,
    currentTenant,
    tenants,
  });

  return (
    <>
      <ViewerSignalR
        constructionSiteId={Number(params.constructionSiteId)}
        folderId={panelsFileInfo?.fileInfo?.ApiFolderId || ''}
      />
      <TabsShell
        ConstructionSiteId={params.constructionSiteId}
        active={PathActiveEnum.panels}
        loading={isLoading}
        panelName={panel?.Name}
      />
      <Container>
        {isInternalItem && panelsFileInfo && activeSrcTypeOption && (
          <>
            <Header
              file={panelsFileInfo}
              buttons={buttons}
              activeListButton={activeListButton}
              onActiveListButton={handleActiveListButton}
              onOpenFolder={handleOpenFolder}
            />
            {isViewerRender && (
              <>
                {(
                  (panelsFileInfo.extension === 'pdf' && !panelsFileInfo.bucketFileName) ||
                  ((panelsFileInfo.extension !== 'pdf') && (
                    (panelsFileInfo?.isRendering && (fileViewerAutodesk?.etapa ?? 0) < 4) ||
                    (!panelsFileInfo?.isRendering && (fileViewerAutodesk?.etapa ?? 0) === 5) ||
                    (panelsFileInfo?.fileRenderStatus === null && (fileViewerAutodesk?.etapa ?? 0) !== 5)))
                ) ? (
                  <LoadingRender fileViewerAutodesk={fileViewerAutodesk} />
                ) : (
                  <SimpleViewerComponent
                    fileInfo={panelsFileInfo}
                    userInfo={userInfo}
                    currentTenant={currentTenant}
                    tenantIds={tenants && tenants.length > 0
                      ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
                      : undefined
                    }
                  />
                )}
              </>
            )}
            {!isViewerRender && (
              <>
                {extensionsImage.includes(panelsFileInfo.extension) && (
                  <Image
                    src={`${process.env.REACT_APP_BASE_API_URL}/Viewer/FileViewer?fileInfoViewer=${panelsFileInfo.fileViewer}`}
                    onDownload={handleDownload}
                  />
                )}
                {!extensionsImage.includes(panelsFileInfo.extension) && (
                  <>
                    {panelsFileInfo.extension === 'pdf' && (
                      <iframe
                        title="pdfViewer"
                        width="100%"
                        height="100%"
                        src={`${process.env.REACT_APP_BASE_API_URL}/Viewer/FileViewer?fileInfoViewer=${panelsFileInfo.fileViewer}${hasDownloadPermission ? '' : '#toolbar=0'}`}
                      />
                    )}
                    {panelsFileInfo.extension !== 'pdf' && (
                      <iframe
                        title="pdfViewer"
                        width="100%"
                        height="100%"
                        src={`${viewerGoogle}${process.env.REACT_APP_BASE_API_URL}/Viewer/FileViewer?fileInfoViewer=${panelsFileInfo.fileViewer}`}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
        {!isInternalItem && activeSrcTypeOption && (
          <>
            {isDropboxType && (
              <DropboxFolderEmbed link={stringContent} />
            )}
            {!isDropboxType && (
              <>
                {activeSrcTypeOption === 'html' && stringContent && (
                  <iframe
                    title={panel?.Name}
                    srcDoc={stringContent}
                    width="100%"
                    height="100%"
                    referrerPolicy="no-referrer"
                    sandbox="allow-scripts"
                  />
                )}
                {activeSrcTypeOption === 'url' && stringContent && (
                  <iframe
                    title={panel?.Name}
                    src={stringContent}
                    width="100%"
                    height="100%"
                  />
                )}
              </>
            )}
          </>
        )}
      </Container>
    </>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  ...state.panels,
  userInfo: state.auth.userInfo,
  globalLoading: state.activities.externalGlobalLoading,
  currentTenant: state.tenant.currentListTenant,
  tenants: state.tenant.Tenants,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

export default connector(Panels);
